import { withTheme } from  'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { useCookies } from 'react-cookie';
import { Fragment, useMemo, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
    FovLoader,
    HorizontalSpacer,
    VerticalSpacer,
    PlusOutlined,
    H3,
    H4,
    FovDivider,
} from 'components';
import { ArrowLeftOutlined } from 'components/icons';

import { getCreatorsAdmin, clearAdminModels, getModelsAdmin } from 'store/actions/admin';
import {
    MemberContainer,
    AddCreatorButton,
    TitleContainer,
    TitleRow,
    IconButton,
    TabContainer,
    Tab,
} from './styles';
import { listColumns } from './listColumns';
import { AddCreatorForm } from './AddCreatorForm';
import { Settings } from './Settings';
import { CreatorModels } from './CreatorModels';

const AdminCreators = withTheme(props => {
    const { theme } = props;
    const dispatch = useDispatch();

    const userDataStore = useSelector(state => state.userData);
    const adminDataStore = useSelector(state => state.adminData);

    const [ cookies ] = useCookies([ 'userAuth' ]);
    const token = cookies.archvision_token;

    const selectedTheme = userDataStore.userPreferences.theme;
    const creators = useMemo(() => adminDataStore.creators, [ adminDataStore.creators ]);
    const isLoading = useMemo(() => adminDataStore.isLoading, [ adminDataStore.isLoading ]);
    const pagination = useMemo(() => adminDataStore.creatorsPagination, [ adminDataStore.creatorsPagination ]);
    const { page, pageSize, totalCount } = pagination;

    const [ showAddCreatorForm, setShowAddCreatorForm ] = useState(false);
    const [ selectedCreator, setSelectedCreator ] = useState(null);
    const [ selectedTab, setSelectedTab ] = useState('settings');
    const [ searchParams, setSearchParams ] = useSearchParams();


    useEffect(() => {
        if (!creators || creators.length === 0) {
            dispatch(getCreatorsAdmin(token));
        }
    }, []);

    const handlePageChange = page => {
        if (Number(searchParams.get('page')) === page) return;
        searchParams.set('page', page);
        setSearchParams(searchParams);
        dispatch(getCreatorsAdmin(token));
    };

    const tabIsSelected = name => selectedTab === name;


    return (
        <FovLoader
            text='Retrieving creators data'
            resource={!isLoading}
        >
            {creators?.length === 0 && <div>No creators.</div>}
            {creators?.length > 0 && !selectedCreator &&
                <Fragment>
                    <TitleRow>
                        <H3>Creators</H3>
                        <AddCreatorButton
                            $selectedTheme={selectedTheme}
                            onClick={() => {
                                setShowAddCreatorForm(true);
                            }}
                        >
                            <>Add Creator</>
                            <HorizontalSpacer size={6} />
                            <PlusOutlined width={20}/>
                        </AddCreatorButton>
                    </TitleRow>
                    <VerticalSpacer size={7} />
                    <MemberContainer
                        dataSource={creators}
                        $selectedTheme={selectedTheme}
                        columns={listColumns(theme, selectedTheme, setSelectedCreator)}
                        rowKey={'row-' + Math.random()}
                        currentPage={page}
                        pageSize={pageSize}
                        totalCount={totalCount}
                        isPagination={true}
                        selectedKey='creator_id'
                        selectedGuid={selectedCreator?.creator_id}
                        onPageChange={handlePageChange}
                    />
                    <AddCreatorForm
                        isVisible={showAddCreatorForm}
                        onClose={() => setShowAddCreatorForm(false)}
                    />
                </Fragment>
            }
            {selectedCreator &&
                <Fragment>
                    <TitleContainer>
                        <H4>{selectedCreator.display_name}</H4>
                        <IconButton
                            theme={theme}
                            $selectedTheme={selectedTheme}
                            backgroundColor={'onPrimary'}
                            hoverColor={theme.colors[selectedTheme].accentHover}
                            onClick={() => {
                                setSelectedCreator(null);
                                searchParams.delete('creatorId');
                                searchParams.delete('page');
                                setSearchParams(searchParams);
                                setSelectedTab('settings');
                                dispatch(clearAdminModels());
                            }}
                            icon={
                                <ArrowLeftOutlined
                                    theme={theme}
                                    color={theme.colors[selectedTheme].primary}
                                />
                            }
                        />
                    </TitleContainer>
                    <TabContainer>
                        <Tab
                            theme={theme}
                            $selectedTheme={selectedTheme}
                            onClick={() => {
                                setSelectedTab('settings');
                            }}
                            selected={tabIsSelected('settings')}
                        >
                            Settings
                        </Tab>
                        <Tab
                            theme={theme}
                            $selectedTheme={selectedTheme}
                            onClick={() => {
                                setSelectedTab('models');
                                searchParams.set('creatorId', selectedCreator.creator_id);
                                setSearchParams(searchParams);
                                dispatch(getModelsAdmin(token));
                            }}
                            selected={tabIsSelected('models')}
                        >
                            Models
                        </Tab>
                    </TabContainer>
                    <FovDivider />
                    <VerticalSpacer size={16}/>
                    {selectedTab === 'settings' && <Settings creator={selectedCreator}/>}
                    {selectedTab === 'models' && <CreatorModels creator={selectedCreator}/>}
                </Fragment>
            }
        </FovLoader>
    );
});

export default AdminCreators;
